/* src/components/sessions/ExportButton.js */
import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faFileCsv,
    faDownload,
    faSpinner,
    faCheck,
    faInfoCircle,
    faExclamationTriangle // For error state
} from '@fortawesome/free-solid-svg-icons';

// Function to convert array of objects to CSV string
const convertToCSV = (data, headers) => {
    if (!Array.isArray(data) || data.length === 0) return '';

    const headerRow = headers.map(h => `"${h.label}"`).join(',');
    const dataRows = data.map(row => {
        return headers.map(header => {
            let value = row[header.key] ?? ''; // Get value or empty string

            // Format specific types if needed
            if (value instanceof Date) {
                value = value.toISOString(); // Standard ISO format
            } else if (typeof value === 'number') {
                // Handle potential floating point precision issues if needed
                if (header.key.toLowerCase().includes('accuracy') || header.key.toLowerCase().includes('rate')) {
                    value = value.toFixed(3); // Example: 3 decimal places for rates/accuracy
                } else if (header.key.toLowerCase().includes('dprime') || header.key.toLowerCase().includes('difficulty')) {
                     value = value.toFixed(4); // Example: 4 decimal places for dprime/difficulty
                }
            } else if (typeof value === 'string' && value.includes(',')) {
                // Escape double quotes within strings containing commas
                value = `"${value.replace(/"/g, '""')}"`;
            } else if (typeof value === 'boolean') {
                 value = value ? 'TRUE' : 'FALSE';
            } else if (typeof value === 'object' && value !== null) {
                 // Stringify objects, escape quotes - BE CAREFUL with deeply nested objects if not intended
                 try {
                    value = `"${JSON.stringify(value).replace(/"/g, '""')}"`;
                 } catch (e) {
                    console.warn(`Could not stringify object for CSV export (key: ${header.key}):`, value);
                    value = '"[Object]"'; // Placeholder for complex objects
                 }
            }
            // Ensure value is stringified before potentially adding quotes
            value = String(value);
            // Only add quotes if value contains comma, newline, or double quote after potential modifications
            // and it wasn't already quoted (handles the object/string case above)
            if (!value.startsWith('"') && (value.includes(',') || value.includes('\n') || value.includes('"'))) {
                value = `"${value.replace(/"/g, '""')}"`;
            }

            return value;
        }).join(',');
    });

    return [headerRow, ...dataRows].join('\n');
};

// Function to trigger CSV download
const downloadCSV = (csvString, filename = 'sessions.csv') => {
    const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    if (link.download !== undefined) { // Feature detection
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', filename);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url); // Clean up
    } else {
        alert("CSV download is not supported in this browser.");
    }
};

// --- MODIFIED ExportButton Component ---
const ExportButton = ({
    profileId,                  // NEW: ID of the profile to export sessions for
    startDate,                  // NEW: Date object or null
    endDate,                    // NEW: Date object or null
    fetchAllSessionsForExport,  // NEW: Function to fetch all data
    filenamePrefix = "dualnback_sessions",
    disabled = false,           // Receive disabled state from parent
    className = ""              // Receive className from parent
}) => {
    const [exportState, setExportState] = useState('idle'); // 'idle', 'fetching', 'success', 'error'
    const [errorMessage, setErrorMessage] = useState('');
    const [tooltipVisible, setTooltipVisible] = useState(false);
    const [exportedCount, setExportedCount] = useState(0); // Track how many sessions were fetched

    const handleExport = async () => {
        // Check for necessary props received from parent
        if (!profileId || !fetchAllSessionsForExport) {
            alert("Cannot export: Missing profile information or export function.");
            setExportState('error');
            setErrorMessage("Configuration error.");
            return;
        }

        setExportState('fetching');
        setErrorMessage('');
        setExportedCount(0);

        try {
            // 1. Fetch ALL sessions using the new function passed via props
            console.log(`ExportButton: Calling fetchAllSessionsForExport for profile ${profileId}`);
            // Pass the Date objects directly; the context function handles conversion
            const result = await fetchAllSessionsForExport(profileId, startDate, endDate);

            if (!result.success || !Array.isArray(result.sessions)) {
                // Handle failure reported by the fetch function
                throw new Error(result.error || "Failed to fetch sessions for export.");
            }

            const sessionsToExport = result.sessions;
            setExportedCount(sessionsToExport.length); // Store the count of fetched sessions

            // Handle case where filters result in no sessions
            if (sessionsToExport.length === 0) {
                alert("No sessions found matching the selected date range to export.");
                setExportState('idle'); // Reset state, nothing to download
                return;
            }

            console.log(`ExportButton: Fetched ${sessionsToExport.length} sessions. Preparing CSV.`);

            // 2. Define headers (Ensure these match keys in the fetched session data)
            const headers = [
                { key: 'id', label: 'Session ID' },
                { key: 'recordedAt', label: 'Date' }, // Expecting ISO string from backend
                { key: 'nBack', label: 'N-Back' },
                { key: 'difficulty', label: 'Difficulty' },
                { key: 'durationMs', label: 'Duration (ms)' },
                { key: 'accuracy', label: 'Overall Accuracy (%)' },
                { key: 'visualAccuracy', label: 'Visual Accuracy (%)' },
                { key: 'audioAccuracy', label: 'Audio Accuracy (%)' },
                { key: 'visualDprime', label: 'Visual d-prime' },
                { key: 'audioDprime', label: 'Audio d-prime' },
                { key: 'overallDprime', label: 'Overall d-prime' },
                { key: 'visualCorrect', label: 'Visual Hits' },
                { key: 'visualTargetCount', label: 'Visual Targets' },
                { key: 'visualFalseAlarms', label: 'Visual False Alarms' },
                { key: 'audioCorrect', label: 'Audio Hits' },
                { key: 'audioTargetCount', label: 'Audio Targets' },
                { key: 'audioFalseAlarms', label: 'Audio False Alarms' },
                { key: 'newNBack', label: 'Resulting N-Back' },
                { key: 'newDifficulty', label: 'Resulting Difficulty' },
                { key: 'consecutiveSuccesses', label: 'Resulting Consecutive Successes' },
                { key: 'completedEarly', label: 'Completed Early' },
                { key: 'trialsCompleted', label: 'Trials Completed' },
                { key: 'totalTrialsPlanned', label: 'Trials Planned' },
                { key: 'isPlayground', label: 'Is Playground' },
                // Add other relevant fields from your session data object if needed
                // e.g., { key: 'replayData', label: 'Replay Data (JSON)' } // Note: JSON stringified by convertToCSV
            ];

            // Artificial delay (optional, keep if desired for UX feedback)
            // await new Promise(resolve => setTimeout(resolve, 300));

            // 3. Convert fetched data to CSV using the helper
            const csvString = convertToCSV(sessionsToExport, headers);
            const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
            const filename = `${filenamePrefix}_${timestamp}.csv`;

            // 4. Trigger Download using the helper
            downloadCSV(csvString, filename);

            // 5. Set Success State and reset after timeout
            setExportState('success');
            setTimeout(() => {
                setExportState('idle');
            }, 3000); // Reset after 3 seconds

        } catch (error) {
            console.error("Error during export process:", error);
            setErrorMessage(error.message || "An error occurred during export.");
            setExportState('error');
            // Optionally auto-reset error state after a while, or require user interaction
            // setTimeout(() => { if (exportState === 'error') setExportState('idle'); }, 5000);
        }
    };

    // Helper to determine button appearance and props based on state
    const getButtonState = () => {
        const baseClasses = "flex items-center justify-center px-4 py-2 rounded-md shadow-sm text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors w-full sm:w-auto";
        switch (exportState) {
            case 'fetching':
                return { icon: faSpinner, text: 'Exporting...', classes: `${baseClasses} bg-gray-400 text-white cursor-wait`, props: { disabled: true, title: "Fetching data for export..." } };
            case 'success':
                // Show the count of exported items in the success state title
                return { icon: faCheck, text: 'Exported!', classes: `${baseClasses} bg-green-600 text-white`, props: { disabled: true, title: `Successfully exported ${exportedCount} sessions.` } };
            case 'error':
                // Show the specific error message in the title
                return { icon: faExclamationTriangle, text: 'Error', classes: `${baseClasses} bg-red-600 text-white hover:bg-red-700 focus:ring-red-500`, props: { title: `Export failed: ${errorMessage}` } };
            case 'idle':
            default:
                 // Check the disabled prop passed from the parent AND if profileId is missing
                 if (disabled || !profileId) {
                     return { icon: faFileCsv, text: 'Export CSV', classes: `${baseClasses} bg-gray-200 text-gray-500 cursor-not-allowed`, props: { disabled: true, title: "Export unavailable (loading page or no profile selected)" } };
                 }
                 // Enabled state
                return { icon: faFileCsv, text: 'Export CSV', classes: `${baseClasses} bg-indigo-600 text-white hover:bg-indigo-700 focus:ring-indigo-500`, props: { title: "Export sessions to CSV based on date filters" } };
        }
    };

    const buttonState = getButtonState();

    // Helper for tooltip date display - formats Date objects
    const formatDateForDisplay = (date) => {
        if (!date || !(date instanceof Date) || isNaN(date)) return '';
        return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
    };

    // Dynamically generate tooltip text based on whether date filters are active
    const tooltipText = startDate || endDate
        ? `Export all sessions within the selected date range (${formatDateForDisplay(startDate) || 'Beginning'} - ${formatDateForDisplay(endDate) || 'Present'}).`
        : "Export all sessions as a CSV file, including all available metrics.";


    return (
        <div className={`relative ${className}`}> {/* Apply className received from parent */}
            <button
                onClick={handleExport}
                // Disable button if parent says so, or if in fetching/success state
                disabled={buttonState.props.disabled || exportState === 'fetching' || exportState === 'success'}
                className={buttonState.classes}
                title={buttonState.props.title} // Dynamic title based on state/error
                onMouseEnter={() => !buttonState.props.disabled && setTooltipVisible(true)} // Show tooltip only if not disabled by logic/parent
                onMouseLeave={() => setTooltipVisible(false)}
                onFocus={() => !buttonState.props.disabled && setTooltipVisible(true)}
                onBlur={() => setTooltipVisible(false)}
            >
                <FontAwesomeIcon icon={buttonState.icon} className={`h-4 w-4 mr-2 ${exportState === 'fetching' ? 'animate-spin' : ''}`} />
                {buttonState.text}
            </button>

            {/* Info Tooltip - improved visibility condition */}
            {tooltipVisible && exportState === 'idle' && !buttonState.props.disabled && ( // Show tooltip only in idle state and when button is actually enabled
                <div className="absolute z-20 w-64 p-3 mt-2 bg-gray-800 rounded-lg shadow-lg text-white text-xs -left-24 sm:left-auto sm:-right-10 top-full">
                    <div className="flex items-start">
                        <FontAwesomeIcon icon={faInfoCircle} className="h-4 w-4 mr-2 mt-0.5 text-gray-300 flex-shrink-0" />
                        <span>
                           {tooltipText} {/* Dynamic tooltip text */}
                        </span>
                    </div>
                    {/* Arrow */}
                    <div className="absolute w-3 h-3 bg-gray-800 transform rotate-45 -mt-[22px] left-1/2 -translate-x-1/2"></div>
                </div>
            )}
        </div>
    );
};

export default ExportButton;